import { enqueueRequest } from "./requestQueue";

const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_RETRY_DELAY_MS = 300;
const MAX_ATTEMPTS = 3;
const RETRYABLE_STATUSES = [429, 500, 502, 503, 504];

/**
 * Erreur renvoyée quand l'API répond un statut d'erreur
 */
export class HttpError extends Error {
  constructor (status, url) {
    super(`Requête ${url} en échec (${status})`);
    this.name = "HttpError";
    this.status = status;
  }
}

const inFlightRequests = new Map();
const cachedResponses = new Map();

/**
 * Récupère le JSON d'une URL de l'API MPG
 * Les appels simultanés sur une même URL partagent une seule requête, les réponses
 * peuvent être gardées en cache, et une requête qui n'aboutit pas est abandonnée
 * puis réessayée pour ne jamais bloquer un calcul
 * @param {String} url l'URL à appeler
 * @param {Object} options le token, la durée de cache et les délais
 * @returns {Promise<Object>} le JSON de la réponse
 */
export const getJson = (url, { token, cacheTtlMs = 0, timeoutMs = DEFAULT_TIMEOUT_MS, retryDelayMs = DEFAULT_RETRY_DELAY_MS } = {}) => {
  const cachedResponse = getCachedResponse(url);
  if (cachedResponse) {
    return Promise.resolve(cachedResponse.value);
  }

  const inFlightRequest = inFlightRequests.get(url);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = enqueueRequest(() => fetchJsonWithRetry(url, {
    token,
    timeoutMs,
    retryDelayMs,
  }))
    .then((data) => {
      if (cacheTtlMs > 0) {
        cachedResponses.set(url, {
          value: data,
          expiresAt: Date.now() + cacheTtlMs,
        });
      }
      return data;
    })
    .finally(() => inFlightRequests.delete(url));

  inFlightRequests.set(url, request);
  return request;
};

/**
 * Vide le cache des réponses, notamment à la déconnexion
 */
export const clearApiCache = () => {
  cachedResponses.clear();
};

const getCachedResponse = (url) => {
  const cachedResponse = cachedResponses.get(url);
  if (! cachedResponse) {
    return;
  }
  if (cachedResponse.expiresAt > Date.now()) {
    return cachedResponse;
  }
  cachedResponses.delete(url);
};

const fetchJsonWithRetry = async (url, { token, timeoutMs, retryDelayMs }) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fetchJsonOnce(url, token, timeoutMs);
    } catch (error) {
      if (attempt === MAX_ATTEMPTS || ! isRetryable(error)) {
        throw error;
      }
      await wait(retryDelayMs * attempt);
    }
  }
};

const fetchJsonOnce = async (url, token, timeoutMs) => {
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json, text/plain, */*",
        ...(token ? { authorization: token } : {}),
      },
      signal: abortController.signal,
    });
    if (! response.ok) {
      throw new HttpError(response.status, url);
    }
    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

const isRetryable = (error) => {
  if (error instanceof HttpError) {
    return RETRYABLE_STATUSES.includes(error.status);
  }
  return error.name === "AbortError" || error.name === "TypeError";
};

const wait = (delayMs) => new Promise(resolve => setTimeout(resolve, delayMs));